import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAccount } from 'wagmi';
import { hyperliquidClient } from '../services/hyperliquid';

// Types for user data
interface Position {
  coin: string;
  szi: string;
  entryPx: string;
  unrealizedPnl: string;
  leverage: number;
  liquidationPx: string | null;
  returnOnEquity: string;
  marginUsed: string;
  cumFunding: string;
}

interface Trade {
  closedPnl: string;
  coin: string;
  dir: string;
  px: string;
  sz: string;
  fee: string;
  tid: number;
  time: number;
  startPosition: string;
  oid: number;
  crossed: boolean;
  hash: string;
}

interface FundingEntry {
  time: number;
  coin: string;
  usdc: string;
  szi: string;
  fundingRate: string;
}

interface OrderEntry {
  order: {
    coin: string;
    side: 'A' | 'B';
    limitPx: string;
    sz: string;
    oid: number;
    timestamp: number;
    origSz: string;
  };
  status: string;
  statusTimestamp: number;
}

interface OpenOrder {
  coin: string;
  limitPx: string;
  oid: number;
  side: 'A' | 'B';
  sz: string;
  timestamp: number;
}

interface UserDataState {
  // Account state
  balance: number;
  accountValue: number;
  marginUsed: number;
  positions: Position[];

  // History
  trades: Trade[];
  fundingHistory: FundingEntry[];
  orderHistory: OrderEntry[];
  openOrders: OpenOrder[];

  // Loading states
  isLoading: boolean;
  isInitialLoad: boolean;
  lastUpdated: number | null;

  // Connection
  isConnected: boolean;
  address: string | undefined;

  // Actions
  refresh: () => Promise<void>;
}

const UserDataContext = createContext<UserDataState | undefined>(undefined);

export function UserDataProvider({ children }: { children: ReactNode }) {
  const { address, isConnected } = useAccount();

  // Account state
  const [balance, setBalance] = useState(0);
  const [accountValue, setAccountValue] = useState(0);
  const [marginUsed, setMarginUsed] = useState(0);
  const [positions, setPositions] = useState<Position[]>([]);

  // History data
  const [trades, setTrades] = useState<Trade[]>([]);
  const [fundingHistory, setFundingHistory] = useState<FundingEntry[]>([]);
  const [orderHistory, setOrderHistory] = useState<OrderEntry[]>([]);
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Fetch all user data
  const fetchAllUserData = useCallback(async (showLoading = true) => {
    if (!address) return;

    if (showLoading) setIsLoading(true);

    try {
      // Fetch all data in parallel
      const [
        userState,
        userFills,
        userFunding,
        historicalOrders,
        userOpenOrders,
      ] = await Promise.all([
        hyperliquidClient.getUserState(address),
        hyperliquidClient.getUserFills(address),
        hyperliquidClient.getUserFunding(address).catch(() => []), // May not be available
        hyperliquidClient.getHistoricalOrders(address).catch(() => []), // May not be available
        hyperliquidClient.getOpenOrders(address).catch(() => []),
      ]);

      // Process user state
      setBalance(parseFloat(userState.withdrawable));
      setAccountValue(parseFloat(userState.marginSummary.accountValue));
      setMarginUsed(parseFloat(userState.marginSummary.totalMarginUsed));

      // Extract active positions
      const activePositions = userState.assetPositions
        .filter(ap => parseFloat(ap.position.szi) !== 0)
        .map(ap => ({
          coin: ap.position.coin,
          szi: ap.position.szi,
          entryPx: ap.position.entryPx,
          unrealizedPnl: ap.position.unrealizedPnl,
          leverage: ap.position.leverage.value,
          liquidationPx: ap.position.liquidationPx,
          returnOnEquity: ap.position.returnOnEquity,
          marginUsed: ap.position.marginUsed,
          cumFunding: ap.position.cumFunding?.allTime || '0',
        }));
      setPositions(activePositions);

      // Process trades (sort by time descending)
      const sortedTrades = [...userFills].sort((a, b) => b.time - a.time);
      setTrades(sortedTrades.slice(0, 100));

      // Process funding history
      const sortedFunding = [...userFunding].sort((a, b) => b.time - a.time);
      setFundingHistory(sortedFunding.slice(0, 100));

      // Process order history
      const sortedOrders = [...historicalOrders].sort((a, b) => b.statusTimestamp - a.statusTimestamp);
      setOrderHistory(sortedOrders.slice(0, 100));

      // Process open orders
      setOpenOrders(userOpenOrders);

      setLastUpdated(Date.now());
      setIsInitialLoad(false);

      console.log('[UserData] Fetched all user data successfully');
    } catch (error) {
      console.error('[UserData] Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  // Clear data when disconnected
  const clearData = useCallback(() => {
    setBalance(0);
    setAccountValue(0);
    setMarginUsed(0);
    setPositions([]);
    setTrades([]);
    setFundingHistory([]);
    setOrderHistory([]);
    setOpenOrders([]);
    setLastUpdated(null);
    setIsInitialLoad(true);
  }, []);

  // Fetch on wallet connect
  useEffect(() => {
    if (isConnected && address) {
      console.log('[UserData] Wallet connected, fetching all data...');
      fetchAllUserData();
    } else {
      clearData();
    }
  }, [isConnected, address, fetchAllUserData, clearData]);

  // Background refresh every 15 seconds (for positions/balance)
  // Less frequent for history (every 60 seconds)
  useEffect(() => {
    if (!isConnected || !address) return;

    // Quick refresh for account state (positions, balance)
    const quickRefresh = setInterval(async () => {
      try {
        const userState = await hyperliquidClient.getUserState(address);
        setBalance(parseFloat(userState.withdrawable));
        setAccountValue(parseFloat(userState.marginSummary.accountValue));
        setMarginUsed(parseFloat(userState.marginSummary.totalMarginUsed));

        const activePositions = userState.assetPositions
          .filter(ap => parseFloat(ap.position.szi) !== 0)
          .map(ap => ({
            coin: ap.position.coin,
            szi: ap.position.szi,
            entryPx: ap.position.entryPx,
            unrealizedPnl: ap.position.unrealizedPnl,
            leverage: ap.position.leverage.value,
            liquidationPx: ap.position.liquidationPx,
            returnOnEquity: ap.position.returnOnEquity,
            marginUsed: ap.position.marginUsed,
            cumFunding: ap.position.cumFunding?.allTime || '0',
          }));
        setPositions(activePositions);

        // Also refresh open orders
        const userOpenOrders = await hyperliquidClient.getOpenOrders(address);
        setOpenOrders(userOpenOrders);

        setLastUpdated(Date.now());
      } catch (error) {
        console.error('[UserData] Quick refresh error:', error);
      }
    }, 15000);

    // Full refresh for history data (less frequent)
    const fullRefresh = setInterval(() => {
      fetchAllUserData(false); // Don't show loading indicator
    }, 60000);

    return () => {
      clearInterval(quickRefresh);
      clearInterval(fullRefresh);
    };
  }, [isConnected, address, fetchAllUserData]);

  return (
    <UserDataContext.Provider
      value={{
        balance,
        accountValue,
        marginUsed,
        positions,
        trades,
        fundingHistory,
        orderHistory,
        openOrders,
        isLoading,
        isInitialLoad,
        lastUpdated,
        isConnected,
        address,
        refresh: () => fetchAllUserData(true),
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
}

export function useUserData(): UserDataState {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
}
